import React, { useEffect, useMemo, useState } from "react";
import {
  BadgePoundSterling,
  Check,
  CreditCard,
  Layers3,
  LoaderCircle,
  Lock,
  Plus,
} from "lucide-react";
import AdminOnly from "../../../components/guards/AdminOnly";
import UserTopNav from "../../../components/layout/UserTopNav";
import AdminSectionCard from "../components/AdminSectionCard";
import {
  listMembershipPlans,
  MembershipPlan,
  saveMembershipPlan,
} from "../services/memberships";

type FormState = {
  id?: string;
  gymId: string;
  name: string;
  slug: string;
  stripePriceId: string;
  tierCode: string;
  interval: "month" | "year";
  status: "active" | "archived";
  appAccess: "none" | "limited" | "full";
  canBookClasses: boolean;
  allowedClassTags: string;
  weeklyBookingLimit: string;
  priority: string;
};

const EMPTY_FORM: FormState = {
  gymId: "default",
  name: "",
  slug: "",
  stripePriceId: "",
  tierCode: "",
  interval: "month",
  status: "active",
  appAccess: "full",
  canBookClasses: true,
  allowedClassTags: "all",
  weeklyBookingLimit: "",
  priority: "0",
};

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function toFormState(plan: MembershipPlan): FormState {
  return {
    id: plan.id,
    gymId: plan.gymId || "default",
    name: plan.name,
    slug: plan.slug,
    stripePriceId: plan.stripePriceId,
    tierCode: plan.tierCode,
    interval: plan.interval,
    status: plan.status,
    appAccess: plan.appAccess,
    canBookClasses: plan.bookingPolicy.canBookClasses,
    allowedClassTags:
      plan.bookingPolicy.allowedClassTags.length > 0
        ? plan.bookingPolicy.allowedClassTags.join(", ")
        : "all",
    weeklyBookingLimit:
      typeof plan.bookingPolicy.weeklyBookingLimit === "number"
        ? String(plan.bookingPolicy.weeklyBookingLimit)
        : "",
    priority: String(plan.priority),
  };
}

export default function AdminMemberships() {
  const [plans, setPlans] = useState<MembershipPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const data = await listMembershipPlans();
        if (!alive) return;

        setPlans(data);

        if (data[0]) {
          setSelectedId(data[0].id);
          setForm(toFormState(data[0]));
        }
      } catch (err: any) {
        if (alive) {
          setError(err?.message || "Failed to load membership plans.");
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const activePlans = useMemo(
    () => plans.filter((plan) => plan.status === "active").length,
    [plans]
  );

  const archivedPlans = plans.length - activePlans;

  function handleNewPlan() {
    setSelectedId(null);
    setForm(EMPTY_FORM);
    setError(null);
    setSuccess(null);
  }

  function handleSelectPlan(plan: MembershipPlan) {
    setSelectedId(plan.id);
    setForm(toFormState(plan));
    setError(null);
    setSuccess(null);
  }

  function updateForm<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => {
      const next = { ...current, [key]: value };

      if (key === "name" && (!current.slug || current.slug === slugify(current.name))) {
        next.slug = slugify(String(value));
      }

      if (key === "name" && !current.tierCode) {
        next.tierCode = slugify(String(value)).toUpperCase().replace(/-/g, "_");
      }

      return next;
    });
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const payload = {
        id: form.id,
        gymId: form.gymId.trim() || "default",
        name: form.name.trim(),
        slug: slugify(form.slug || form.name),
        stripePriceId: form.stripePriceId.trim(),
        tierCode: form.tierCode.trim() || slugify(form.name).toUpperCase().replace(/-/g, "_"),
        interval: form.interval,
        status: form.status,
        appAccess: form.appAccess,
        canBookClasses: form.canBookClasses,
        allowedClassTags: form.allowedClassTags
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean),
        weeklyBookingLimit: form.weeklyBookingLimit
          ? Number(form.weeklyBookingLimit)
          : null,
        priority: Number(form.priority) || 0,
      } as const;

      if (!payload.name) {
        throw new Error("Plan name is required.");
      }

      if (!payload.stripePriceId) {
        throw new Error("Stripe price ID is required.");
      }

      const savedId = await saveMembershipPlan(payload);
      const refreshed = await listMembershipPlans();
      setPlans(refreshed);

      const savedPlan = refreshed.find((plan) => plan.id === savedId);
      if (savedPlan) {
        setSelectedId(savedPlan.id);
        setForm(toFormState(savedPlan));
      }

      setSuccess(payload.id ? "Membership plan updated." : "Membership plan created.");
    } catch (err: any) {
      setError(err?.message || "Failed to save membership plan.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminOnly>
      <div className="min-h-screen bg-black text-white">
        <UserTopNav />

        <div className="px-3 py-5 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-neutral-950 p-4 sm:rounded-[2rem] sm:p-8">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.14),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.10),transparent_28%)]" />
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/60 to-transparent" />

              <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                <div className="max-w-2xl min-w-0">
                  <div className="inline-flex items-center gap-2 rounded-full border border-sky-500/20 bg-sky-500/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-sky-200 sm:text-[11px] sm:tracking-[0.28em]">
                    <BadgePoundSterling className="h-3.5 w-3.5 shrink-0" />
                    Admin Memberships
                  </div>

                  <h1 className="mt-4 text-2xl font-heading tracking-tight sm:text-4xl">
                    Membership Tiers
                  </h1>

                  <p className="mt-3 max-w-2xl text-sm leading-6 text-neutral-400 sm:text-base">
                    Create the subscription tiers each gym sells, map them to Stripe prices,
                    and define what access each plan unlocks across Locker Room.
                  </p>
                </div>

                <div className="grid w-full grid-cols-2 gap-3 lg:w-auto lg:min-w-[360px] lg:grid-cols-3">
                  <div className="rounded-2xl border border-white/10 bg-black/40 px-4 py-3 backdrop-blur">
                    <div className="text-[11px] uppercase tracking-[0.24em] text-neutral-500">
                      Total plans
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-white">{plans.length}</div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-black/40 px-4 py-3 backdrop-blur">
                    <div className="text-[11px] uppercase tracking-[0.24em] text-neutral-500">
                      Active
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-white">{activePlans}</div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-black/40 px-4 py-3 backdrop-blur">
                    <div className="text-[11px] uppercase tracking-[0.24em] text-neutral-500">
                      Archived
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-white">{archivedPlans}</div>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="mt-6 rounded-3xl border border-white/10 bg-neutral-950 p-6 text-neutral-400">
                Loading membership plans...
              </div>
            ) : (
              <div className="mt-6 grid gap-6 xl:grid-cols-[1.1fr_1.4fr]">
                <AdminSectionCard title="Plan Library">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-neutral-400">
                      Build pricing tiers around how each gym actually sells access.
                    </p>
                    <button
                      type="button"
                      onClick={handleNewPlan}
                      className="inline-flex items-center gap-2 rounded-full border border-sky-500/25 bg-sky-500/10 px-4 py-2 text-sm font-semibold text-sky-100 transition hover:bg-sky-500/15"
                    >
                      <Plus className="h-4 w-4" />
                      New plan
                    </button>
                  </div>

                  <div className="mt-5 space-y-3">
                    {plans.length === 0 ? (
                      <div className="rounded-3xl border border-dashed border-white/10 bg-white/[0.02] p-5 text-sm text-neutral-400">
                        No membership tiers yet. Create your first plan on the right.
                      </div>
                    ) : (
                      plans.map((plan) => {
                        const isSelected = plan.id === selectedId;

                        return (
                          <button
                            key={plan.id}
                            type="button"
                            onClick={() => handleSelectPlan(plan)}
                            className={[
                              "w-full rounded-[1.5rem] border p-4 text-left transition",
                              isSelected
                                ? "border-sky-400/40 bg-sky-500/10"
                                : "border-white/10 bg-white/[0.03] hover:bg-white/[0.05]",
                            ].join(" ")}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <div className="flex items-center gap-2">
                                  <div className="text-base font-semibold text-white">
                                    {plan.name}
                                  </div>
                                  <span
                                    className={[
                                      "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.22em]",
                                      plan.status === "active"
                                        ? "border border-emerald-500/20 bg-emerald-500/10 text-emerald-200"
                                        : "border border-white/10 bg-white/[0.06] text-white/60",
                                    ].join(" ")}
                                  >
                                    {plan.status}
                                  </span>
                                </div>
                                <div className="mt-1 text-sm text-neutral-400">
                                  {plan.tierCode} · {plan.interval} · {plan.appAccess} access
                                </div>
                              </div>

                              {isSelected ? (
                                <div className="rounded-full border border-sky-400/30 bg-sky-400/10 p-2 text-sky-200">
                                  <Check className="h-4 w-4" />
                                </div>
                              ) : null}
                            </div>

                            <div className="mt-4 flex flex-wrap gap-2 text-xs text-neutral-300">
                              <span className="rounded-full border border-white/10 bg-black/30 px-3 py-1">
                                Stripe: {plan.stripePriceId}
                              </span>
                              <span className="rounded-full border border-white/10 bg-black/30 px-3 py-1">
                                {plan.bookingPolicy.canBookClasses ? "Books classes" : "No class booking"}
                              </span>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </AdminSectionCard>

                <AdminSectionCard title={form.id ? "Edit Membership Plan" : "Create Membership Plan"}>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="block">
                        <div className="mb-2 text-sm font-medium text-neutral-300">Plan name</div>
                        <input
                          value={form.name}
                          onChange={(e) => updateForm("name", e.target.value)}
                          placeholder="Unlimited Membership"
                          className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white outline-none transition placeholder:text-neutral-500 focus:border-sky-400/40 focus:bg-white/[0.06]"
                        />
                      </label>

                      <label className="block">
                        <div className="mb-2 text-sm font-medium text-neutral-300">Gym ID</div>
                        <input
                          value={form.gymId}
                          onChange={(e) => updateForm("gymId", e.target.value)}
                          placeholder="default"
                          className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white outline-none transition placeholder:text-neutral-500 focus:border-sky-400/40 focus:bg-white/[0.06]"
                        />
                      </label>

                      <label className="block">
                        <div className="mb-2 text-sm font-medium text-neutral-300">Slug</div>
                        <input
                          value={form.slug}
                          onChange={(e) => updateForm("slug", e.target.value)}
                          placeholder="unlimited-membership"
                          className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white outline-none transition placeholder:text-neutral-500 focus:border-sky-400/40 focus:bg-white/[0.06]"
                        />
                      </label>

                      <label className="block">
                        <div className="mb-2 text-sm font-medium text-neutral-300">Tier code</div>
                        <input
                          value={form.tierCode}
                          onChange={(e) => updateForm("tierCode", e.target.value.toUpperCase())}
                          placeholder="UNLIMITED"
                          className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white outline-none transition placeholder:text-neutral-500 focus:border-sky-400/40 focus:bg-white/[0.06]"
                        />
                      </label>

                      <label className="block md:col-span-2">
                        <div className="mb-2 text-sm font-medium text-neutral-300">Stripe price ID</div>
                        <div className="relative">
                          <CreditCard className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
                          <input
                            value={form.stripePriceId}
                            onChange={(e) => updateForm("stripePriceId", e.target.value)}
                            placeholder="price_123456789"
                            className="w-full rounded-2xl border border-white/10 bg-white/[0.04] py-3 pl-11 pr-4 text-white outline-none transition placeholder:text-neutral-500 focus:border-sky-400/40 focus:bg-white/[0.06]"
                          />
                        </div>
                      </label>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                      <label className="block">
                        <div className="mb-2 text-sm font-medium text-neutral-300">Billing interval</div>
                        <select
                          value={form.interval}
                          onChange={(e) => updateForm("interval", e.target.value as "month" | "year")}
                          className="w-full rounded-2xl border border-white/10 bg-neutral-950 px-4 py-3 text-white outline-none transition focus:border-sky-400/40"
                        >
                          <option value="month">Monthly</option>
                          <option value="year">Yearly</option>
                        </select>
                      </label>

                      <label className="block">
                        <div className="mb-2 text-sm font-medium text-neutral-300">Status</div>
                        <select
                          value={form.status}
                          onChange={(e) => updateForm("status", e.target.value as "active" | "archived")}
                          className="w-full rounded-2xl border border-white/10 bg-neutral-950 px-4 py-3 text-white outline-none transition focus:border-sky-400/40"
                        >
                          <option value="active">Active</option>
                          <option value="archived">Archived</option>
                        </select>
                      </label>

                      <label className="block">
                        <div className="mb-2 text-sm font-medium text-neutral-300">App access</div>
                        <select
                          value={form.appAccess}
                          onChange={(e) =>
                            updateForm("appAccess", e.target.value as "none" | "limited" | "full")
                          }
                          className="w-full rounded-2xl border border-white/10 bg-neutral-950 px-4 py-3 text-white outline-none transition focus:border-sky-400/40"
                        >
                          <option value="full">Full</option>
                          <option value="limited">Limited</option>
                          <option value="none">None</option>
                        </select>
                      </label>

                      <label className="block">
                        <div className="mb-2 text-sm font-medium text-neutral-300">Priority</div>
                        <input
                          type="number"
                          value={form.priority}
                          onChange={(e) => updateForm("priority", e.target.value)}
                          className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white outline-none transition placeholder:text-neutral-500 focus:border-sky-400/40 focus:bg-white/[0.06]"
                        />
                      </label>
                    </div>

                    <div className="rounded-[1.5rem] border border-white/10 bg-white/[0.03] p-4">
                      <div className="flex items-center gap-2 text-sm font-semibold text-white">
                        <Layers3 className="h-4 w-4 text-sky-300" />
                        Booking and access rules
                      </div>

                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        <label className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-neutral-200">
                          <input
                            type="checkbox"
                            checked={form.canBookClasses}
                            onChange={(e) => updateForm("canBookClasses", e.target.checked)}
                            className="h-4 w-4 rounded border-white/20 bg-neutral-950 text-sky-400 focus:ring-sky-400/30"
                          />
                          Plan holders can book classes
                        </label>

                        <label className="block">
                          <div className="mb-2 text-sm font-medium text-neutral-300">Weekly booking limit</div>
                          <input
                            type="number"
                            min="0"
                            value={form.weeklyBookingLimit}
                            onChange={(e) => updateForm("weeklyBookingLimit", e.target.value)}
                            placeholder="Leave blank for unlimited"
                            className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white outline-none transition placeholder:text-neutral-500 focus:border-sky-400/40 focus:bg-white/[0.06]"
                          />
                        </label>

                        <label className="block md:col-span-2">
                          <div className="mb-2 text-sm font-medium text-neutral-300">Allowed class tags</div>
                          <input
                            value={form.allowedClassTags}
                            onChange={(e) => updateForm("allowedClassTags", e.target.value)}
                            placeholder="all, open-gym, coached"
                            className="w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white outline-none transition placeholder:text-neutral-500 focus:border-sky-400/40 focus:bg-white/[0.06]"
                          />
                          <p className="mt-2 text-xs text-neutral-500">
                            Comma separate values. Use <span className="text-neutral-300">all</span> for unrestricted booking.
                          </p>
                        </label>
                      </div>
                    </div>

                    {error ? (
                      <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                        {error}
                      </div>
                    ) : null}

                    {success ? (
                      <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">
                        {success}
                      </div>
                    ) : null}

                    <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
                      <div className="rounded-[1.5rem] border border-white/10 bg-black/20 p-4">
                        <div className="flex items-center gap-2 text-sm font-semibold text-white">
                          <Lock className="h-4 w-4 text-sky-300" />
                          Plan preview
                        </div>
                        <div className="mt-3 text-lg font-semibold text-white">{form.name || "Untitled plan"}</div>
                        <div className="mt-1 text-sm text-neutral-400">
                          {form.interval === "year" ? "Yearly" : "Monthly"} · {form.appAccess} access
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                          <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-neutral-300">
                            {form.canBookClasses ? "Books classes" : "No class booking"}
                          </span>
                          <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-neutral-300">
                            Stripe {form.stripePriceId || "pending"}
                          </span>
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={saving}
                        className="inline-flex min-h-[120px] items-center justify-center gap-3 rounded-[1.5rem] border border-sky-500/30 bg-sky-500/10 px-6 py-4 text-base font-semibold text-sky-50 transition hover:bg-sky-500/15 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {saving ? <LoaderCircle className="h-5 w-5 animate-spin" /> : <BadgePoundSterling className="h-5 w-5" />}
                        {saving ? "Saving membership plan..." : form.id ? "Update membership plan" : "Create membership plan"}
                      </button>
                    </div>
                  </form>
                </AdminSectionCard>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminOnly>
  );
}
